import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    connect() {
        this.$track = $(this.element).find(".preview-slider__blog-track");
        this.$prevBtn = $(this.element).find(".preview-slider__nav-btn--prev");
        this.$nextBtn = $(this.element).find(".preview-slider__nav-btn--next");

        this.prepareTrack();

        if (this.slideCount <= 1) {
            return;
        }

        this.initSlider();
        this.bindNav();
    }

    getSlickInstance() {
        if (!this.$track?.hasClass("slick-initialized")) {
            return null;
        }

        try {
            return this.$track.slick("getSlick");
        } catch (error) {
            return null;
        }
    }

    prepareTrack() {
        const slickInstance = this.getSlickInstance();

        if (slickInstance) {
            this.slideCount = slickInstance.slideCount;
            return;
        }

        if (this.$track.hasClass("slick-initialized")) {
            this.restoreTrackSlides();
        }

        this.slideCount = this.$track.children(".preview-slider__blog-slide").length;
    }

    restoreTrackSlides() {
        const slides = [];
        const seen = new Set();

        this.$track.find(".preview-slider__blog-slide").each((_, slide) => {
            const link = slide.querySelector("a[href]");
            const key = link?.href || slide.textContent.trim();

            if (!key || seen.has(key)) {
                return;
            }

            seen.add(key);
            slides.push(slide.outerHTML);
        });

        if (slides.length === 0) {
            return;
        }

        try {
            if (this.getSlickInstance()) {
                this.$track.slick("unslick");
            }
        } catch (error) {
            // Ignore broken slick instances and rebuild the track markup below.
        }

        this.$track.removeClass("slick-initialized slick-slider").empty();
        slides.forEach((slideHtml) => {
            this.$track.append(slideHtml);
        });
        this.slideCount = slides.length;
    }

    initSlider() {
        if (this.getSlickInstance() || this.initializing) {
            return;
        }

        this.initializing = true;

        try {
            this.$track.slick({
                slidesToShow: 1,
                slidesToScroll: 1,
                infinite: true,
                arrows: false,
                dots: false,
                autoplay: false,
                swipe: true,
                adaptiveHeight: false,
                responsive: [
                    {
                        breakpoint: 768,
                        settings: {
                            adaptiveHeight: true,
                        },
                    },
                ],
            });

            this.refreshSlider = () => {
                if (this.getSlickInstance()) {
                    this.$track.slick("setPosition");
                }
            };

            requestAnimationFrame(() => {
                requestAnimationFrame(this.refreshSlider);
            });

            this.resizeObserver = new ResizeObserver(this.refreshSlider);
            this.resizeObserver.observe(this.element);
            window.addEventListener("resize", this.refreshSlider, { passive: true });
        } finally {
            this.initializing = false;
        }
    }

    bindNav() {
        this.unbindNav();

        this.onPrev = (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (this.getSlickInstance()) {
                this.$track.slick("slickPrev");
            }
        };

        this.onNext = (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (this.getSlickInstance()) {
                this.$track.slick("slickNext");
            }
        };

        this.$prevBtn.on("click.previewSliderBlog", this.onPrev);
        this.$nextBtn.on("click.previewSliderBlog", this.onNext);
    }

    unbindNav() {
        this.$prevBtn?.off("click.previewSliderBlog");
        this.$nextBtn?.off("click.previewSliderBlog");
    }

    disconnect() {
        this.unbindNav();

        window.removeEventListener("resize", this.refreshSlider);
        this.resizeObserver?.disconnect();

        if (this.getSlickInstance()) {
            this.$track.slick("unslick");
        }
    }
}
